import * as XLSX from 'xlsx';
import type { BulkSupportedRow, BulkSupportRequestRow } from './types';
import { EMPTY_BANK_DIRECTORY, type BankDirectory } from './bankResolution';

const HEADER_COLUMNS = {
  israeliId: 'תעודת זהות *',
  firstName: 'שם פרטי *',
  lastName: 'שם משפחה *',
  city: 'עיר *',
  street: 'רחוב',
  houseNumber: 'מספר בית',
  zipCode: 'מיקוד',
  phone: 'טלפון נייד',
  email: 'אימייל',
  accountOwnerName: 'שם בעל החשבון *',
  bankChoice: 'בחר בנק *',
  bankCode: 'קוד בנק (אוטומטי)',
  branchChoice: 'בחר סניף *',
  branchNumber: 'מספר סניף (אוטומטי)',
  accountNumber: 'מספר חשבון *',
  amount: 'סכום *',
};

// Columns are matched by header text with the "required" asterisk stripped, so a file
// saved from an older version of the template still lines up.
function normalizeHeader(header: string): string {
  return header
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*\*+$/, '')
    .trim();
}

function displayName(fieldName: string): string {
  return normalizeHeader(fieldName).replace(/\s*\(.*\)$/, '');
}

function findHeaderRowIndex(rows: unknown[][]): number {
  for (let i = 0; i < Math.min(10, rows.length); i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;
    if (normalizeHeader(String(row[0] ?? '')) === normalizeHeader(HEADER_COLUMNS.israeliId)) {
      return i;
    }
  }
  return -1;
}

function findColumnIndex(headers: string[], target: string): number {
  const normalizedTarget = normalizeHeader(target);
  return headers.findIndex((h) => normalizeHeader(h) === normalizedTarget);
}

// A requirement is met by any one of its alternatives, so the bank can be supplied either by
// the "choose" column or by the computed code column.
type ColumnRequirement = string | string[];

function findMissingColumns(headers: string[], required: ColumnRequirement[]): string[] {
  return required
    .filter((requirement) => {
      const alternatives = Array.isArray(requirement) ? requirement : [requirement];
      return alternatives.every((label) => findColumnIndex(headers, label) < 0);
    })
    .map((requirement) => displayName(Array.isArray(requirement) ? requirement[0] : requirement));
}

function getRequiredString(row: unknown[], index: number, fieldName: string): string {
  if (index < 0) throw new Error(`עמודה "${displayName(fieldName)}" לא נמצאה בקובץ.`);
  const value = row[index];
  if (value == null || String(value).trim() === '') {
    throw new Error(`"${displayName(fieldName)}" הוא שדה חובה.`);
  }
  return String(value).trim();
}

function getOptionalString(row: unknown[], index: number): string {
  if (index < 0) return '';
  const value = row[index];
  return value != null ? String(value).trim() : '';
}

function parsePositiveInteger(value: string): number | null {
  if (!/^\d+$/.test(value)) return null;
  const num = Number(value);
  return num > 0 ? num : null;
}

function getRequiredNumber(row: unknown[], index: number, fieldName: string): number {
  if (index < 0) throw new Error(`עמודה "${displayName(fieldName)}" לא נמצאה בקובץ.`);
  const value = row[index];
  const num = Number(value);
  if (isNaN(num) || num <= 0) {
    throw new Error(`"${displayName(fieldName)}" חייב להיות מספר חיובי.`);
  }
  return num;
}

const SUPPORT_REQUEST_COLUMNS: ColumnRequirement[] = [
  HEADER_COLUMNS.israeliId,
  HEADER_COLUMNS.accountOwnerName,
  [HEADER_COLUMNS.bankCode, HEADER_COLUMNS.bankChoice],
  [HEADER_COLUMNS.branchNumber, HEADER_COLUMNS.branchChoice],
  HEADER_COLUMNS.accountNumber,
  HEADER_COLUMNS.amount,
];

interface BankColumns {
  bankChoice: number;
  bankCode: number;
  branchChoice: number;
  branchNumber: number;
}

function findBankColumns(headers: string[]): BankColumns {
  return {
    bankChoice: findColumnIndex(headers, HEADER_COLUMNS.bankChoice),
    bankCode: findColumnIndex(headers, HEADER_COLUMNS.bankCode),
    branchChoice: findColumnIndex(headers, HEADER_COLUMNS.branchChoice),
    branchNumber: findColumnIndex(headers, HEADER_COLUMNS.branchNumber),
  };
}

// The computed columns hold formulas that read the "choose" columns, and pasting over either
// one wipes them. Reading both, in whatever shape the value arrived, means a row pasted from
// an outside workbook no longer has to be re-keyed by hand.
function resolveBankCode(row: unknown[], cols: BankColumns, directory: BankDirectory): number {
  const computed = parsePositiveInteger(getOptionalString(row, cols.bankCode));
  if (computed != null) return computed;

  const choice = getOptionalString(row, cols.bankChoice);
  const resolved = choice ? directory.bankCodeFromText(choice) : null;
  if (resolved != null) return resolved;

  throw new Error(
    choice
      ? `הבנק "${choice}" לא זוהה. יש לבחור בנק מהרשימה או להזין את קוד הבנק.`
      : '"בחר בנק" הוא שדה חובה.'
  );
}

function resolveBranchNumber(
  row: unknown[],
  cols: BankColumns,
  directory: BankDirectory,
  bankCode: number
): string {
  const computed = parsePositiveInteger(getOptionalString(row, cols.branchNumber));
  if (computed != null) return String(computed);

  const choice = getOptionalString(row, cols.branchChoice);
  const resolved = choice ? directory.branchNumberFromText(bankCode, choice) : null;
  if (resolved != null) return resolved;

  throw new Error(
    choice
      ? `הסניף "${choice}" לא זוהה. יש לבחור סניף מהרשימה או להזין את מספר הסניף.`
      : '"בחר סניף" הוא שדה חובה.'
  );
}

export interface ParseResult<T> {
  rows: T[];
  errors: { rowNumber: number; message: string }[];
}

function isRowEmpty(row: unknown[]): boolean {
  return !row || row.every((cell) => cell == null || String(cell).trim() === '');
}

export function parseSupportedsExcel(
  file: File,
  directory: BankDirectory = EMPTY_BANK_DIRECTORY
): Promise<ParseResult<BulkSupportedRow>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });

        const headerRowIndex = findHeaderRowIndex(rawData);
        if (headerRowIndex < 0) {
          reject(new Error('לא נמצאה שורת כותרות בקובץ. וודאו שהקובץ תואם לתבנית.'));
          return;
        }

        const headers = (rawData[headerRowIndex] as string[]).map(String);
        const cols = {
          israeliId: findColumnIndex(headers, HEADER_COLUMNS.israeliId),
          firstName: findColumnIndex(headers, HEADER_COLUMNS.firstName),
          lastName: findColumnIndex(headers, HEADER_COLUMNS.lastName),
          city: findColumnIndex(headers, HEADER_COLUMNS.city),
          street: findColumnIndex(headers, HEADER_COLUMNS.street),
          houseNumber: findColumnIndex(headers, HEADER_COLUMNS.houseNumber),
          zipCode: findColumnIndex(headers, HEADER_COLUMNS.zipCode),
          phone: findColumnIndex(headers, HEADER_COLUMNS.phone),
          email: findColumnIndex(headers, HEADER_COLUMNS.email),
          accountOwnerName: findColumnIndex(headers, HEADER_COLUMNS.accountOwnerName),
          accountNumber: findColumnIndex(headers, HEADER_COLUMNS.accountNumber),
        };
        const bankCols = findBankColumns(headers);

        const rows: BulkSupportedRow[] = [];
        const errors: { rowNumber: number; message: string }[] = [];

        for (let i = headerRowIndex + 1; i < rawData.length; i++) {
          const raw = rawData[i] as unknown[];
          if (isRowEmpty(raw)) continue;

          const excelRowNumber = i + 1;
          try {
            const bankCode = resolveBankCode(raw, bankCols, directory);
            rows.push({
              israeliId: getRequiredString(raw, cols.israeliId, HEADER_COLUMNS.israeliId),
              firstName: getRequiredString(raw, cols.firstName, HEADER_COLUMNS.firstName),
              lastName: getRequiredString(raw, cols.lastName, HEADER_COLUMNS.lastName),
              city: getOptionalString(raw, cols.city),
              street: getOptionalString(raw, cols.street),
              houseNumber: getOptionalString(raw, cols.houseNumber),
              zipCode: getOptionalString(raw, cols.zipCode),
              phone: getOptionalString(raw, cols.phone),
              email: getOptionalString(raw, cols.email),
              accountOwnerName: getRequiredString(raw, cols.accountOwnerName, HEADER_COLUMNS.accountOwnerName),
              bankCode,
              branchNumber: resolveBranchNumber(raw, bankCols, directory, bankCode),
              accountNumber: getRequiredString(raw, cols.accountNumber, HEADER_COLUMNS.accountNumber),
            });
          } catch (err) {
            errors.push({
              rowNumber: excelRowNumber,
              message: err instanceof Error ? err.message : 'שגיאה בקריאת שורה.',
            });
          }
        }

        resolve({ rows, errors });
      } catch {
        reject(new Error('שגיאה בקריאת קובץ האקסל.'));
      }
    };
    reader.onerror = () => reject(new Error('שגיאה בקריאת הקובץ.'));
    reader.readAsArrayBuffer(file);
  });
}

export function parseSupportRequestsExcel(
  file: File,
  directory: BankDirectory = EMPTY_BANK_DIRECTORY
): Promise<ParseResult<BulkSupportRequestRow>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });

        const headerRowIndex = findHeaderRowIndex(rawData);
        if (headerRowIndex < 0) {
          reject(new Error('לא נמצאה שורת כותרות בקובץ. וודאו שהקובץ תואם לתבנית.'));
          return;
        }

        const headers = (rawData[headerRowIndex] as string[]).map(String);

        // A support request is filed for a supported who already exists in the system, so the
        // template only asks for the identifier, the bank account and the amount. Files made
        // with the older template carry extra personal columns — they are simply ignored.
        const missing = findMissingColumns(headers, SUPPORT_REQUEST_COLUMNS);
        if (missing.length > 0) {
          const names = missing.map((name) => `"${name}"`).join(', ');
          reject(new Error(`העמודות הבאות לא נמצאו בקובץ: ${names}. ודאו שהקובץ הוא תבנית הוספת בקשות תמיכה.`));
          return;
        }

        const cols = {
          israeliId: findColumnIndex(headers, HEADER_COLUMNS.israeliId),
          accountOwnerName: findColumnIndex(headers, HEADER_COLUMNS.accountOwnerName),
          accountNumber: findColumnIndex(headers, HEADER_COLUMNS.accountNumber),
          amount: findColumnIndex(headers, HEADER_COLUMNS.amount),
        };
        const bankCols = findBankColumns(headers);

        const rows: BulkSupportRequestRow[] = [];
        const errors: { rowNumber: number; message: string }[] = [];

        for (let i = headerRowIndex + 1; i < rawData.length; i++) {
          const raw = rawData[i] as unknown[];
          if (isRowEmpty(raw)) continue;

          const excelRowNumber = i + 1;
          try {
            const bankCode = resolveBankCode(raw, bankCols, directory);
            rows.push({
              israeliId: getRequiredString(raw, cols.israeliId, HEADER_COLUMNS.israeliId),
              accountOwnerName: getRequiredString(raw, cols.accountOwnerName, HEADER_COLUMNS.accountOwnerName),
              bankCode,
              branchNumber: resolveBranchNumber(raw, bankCols, directory, bankCode),
              accountNumber: getRequiredString(raw, cols.accountNumber, HEADER_COLUMNS.accountNumber),
              amount: getRequiredNumber(raw, cols.amount, HEADER_COLUMNS.amount),
            });
          } catch (err) {
            errors.push({
              rowNumber: excelRowNumber,
              message: err instanceof Error ? err.message : 'שגיאה בקריאת שורה.',
            });
          }
        }

        resolve({ rows, errors });
      } catch {
        reject(new Error('שגיאה בקריאת קובץ האקסל.'));
      }
    };
    reader.onerror = () => reject(new Error('שגיאה בקריאת הקובץ.'));
    reader.readAsArrayBuffer(file);
  });
}
